import moment from 'moment';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';

import Table from 'atoms/Table';
import ImageDetailsCell from 'atoms/TableCell/ImageDetailsCell';
import SmallLoadingIndicator from 'atoms/SmallLoadingIndicator';
import { headerStyle } from 'containers/App/constants';
import { makeSelectIsMobile } from 'containers/App/selectors';

import { fetchReferrerPatients, fetchReferrerPending } from './actions';
import {
	makeSelectPatients,
	makeSelectPatientsLoading,
	makeSelectPatientsTotal,
	makeSelectPending,
	makeSelectPendingLoading,
} from './selectors';

const statusBadge = (status) => {
	const colors = {
		matched: 'badge-success',
		pending_review: 'badge-warning',
	};
	const cls = colors[status] || 'badge-secondary';
	const label = (status || '').replace(/_/g, ' ');
	return (
		<span className={`badge ${cls} font-10 px-2 py-1 text-uppercase`}>
			{label}
		</span>
	);
};


class ReferrerDashboard extends Component {
	componentDidMount() {
		this.props.dispatch(fetchReferrerPatients());
		this.props.dispatch(fetchReferrerPending());
	}

	render() {
		const {
			patients,
			patientsLoading,
			pending,
			pendingLoading,
		} = this.props;

		const columns = [
			{
				field: 'name',
				title: 'PATIENT',
				headerStyle,
				render: (row) => (
					<Link
						to={`/referrer/dashboard/patient/${row.patientId}`}
						className="hand text-decoration-none"
					>
						<ImageDetailsCell
							heading={row.name || '—'}
						/>
					</Link>
				),
			},
			{
				field: 'levelOfCare',
				title: 'LEVEL OF CARE',
				headerStyle,
				render: (row) => (
					<div className="mt-2 mx-2 font-13">
						{row.levelOfCare || '—'}
					</div>
				),
			},
			{
				field: 'status',
				title: 'STATUS',
				headerStyle,
				render: (row) => (
					<div className="mt-2 mx-2">{statusBadge(row.status)}</div>
				),
			},
			{
				field: 'expiresAt',
				title: 'EXPIRES',
				headerStyle,
				render: (row) => {
					if (!row.expiresAt) return <div className="mt-2 mx-2 font-13">—</div>;
					const daysLeft = moment(row.expiresAt).diff(moment(), 'days');
					const dateStr = moment(row.expiresAt).format('MM/DD/YYYY');
					let cls = 'font-13';
					let suffix = '';
					if (daysLeft < 0) {
						cls = 'font-13 text-danger font-weight-bold';
						suffix = ' (expired)';
					} else if (daysLeft <= 7) {
						cls = 'font-13 text-danger';
						suffix = ` (${daysLeft}d)`;
					} else if (daysLeft <= 30) {
						cls = 'font-13 text-warning';
						suffix = ` (${daysLeft}d)`;
					}
					return (
						<div className={`mt-2 mx-2 ${cls}`}>
							{dateStr}{suffix}
						</div>
					);
				},
			},
		];

		const pendingColumns = [
			{
				field: 'patientName',
				title: 'PATIENT',
				headerStyle,
				render: (row) => (
					<ImageDetailsCell
						heading={row.patientName || '—'}
					/>
				),
			},
			{
				field: 'createdAt',
				title: 'SUBMITTED',
				headerStyle,
				render: (row) => (
					<div className="mt-2 mx-2 font-13">
						{row.createdAt ? moment(row.createdAt).format('MM/DD/YYYY') : '—'}
					</div>
				),
			},
			{
				field: 'status',
				title: 'STATUS',
				headerStyle,
				render: () => (
					<div className="mt-2 mx-2">{statusBadge('pending_review')}</div>
				),
			},
		];

		return (
			<div className="px-5 mt-5">
				<h5 className="mb-3">My Patients</h5>

				{(pending.length > 0 || pendingLoading) && (
					<div className="mb-4">
						<h6 className="text-muted mb-2">Pending Approval</h6>
						{pendingLoading ? (
							<SmallLoadingIndicator />
						) : (
							<Table
								columns={pendingColumns}
								data={pending}
								keyField="referrerPending"
								placeholder="No pending authorizations"
								classes="table-responsive-xl"
							/>
						)}
					</div>
				)}

				<div>
					<h6 className="text-muted mb-2">Active Authorizations</h6>
					{patientsLoading ? (
						<SmallLoadingIndicator />
					) : patients.length === 0 ? (
						<p className="text-muted">
							No active patient authorizations. Patients must sign an ROI
							to grant you access.
						</p>
					) : (
						<Table
							columns={columns}
							data={patients}
							keyField="referrerPatients"
							placeholder="No active patient authorizations"
							classes="table-responsive-xl"
						/>
					)}
				</div>
			</div>
		);
	}
}

const mapStateToProps = createStructuredSelector({
	patients: makeSelectPatients(),
	patientsTotal: makeSelectPatientsTotal(),
	patientsLoading: makeSelectPatientsLoading(),
	pending: makeSelectPending(),
	pendingLoading: makeSelectPendingLoading(),
	isMobile: makeSelectIsMobile(),
});

export default connect(mapStateToProps)(ReferrerDashboard);
